export type ExposureDeviceEventVerdict = 'in-list' | 'not-in-list';

export type ExposureDeviceEventCheckInput = {
  isMultiItem: boolean;
  configuredItemIds: number[] | number[][] | undefined;
  receivedItemId: number;
};

/** Flatten startSession / config itemIds (flat list or matrix) to catalog ids. */
export function flattenConfiguredExposureItemIds(
  itemIds: number[] | number[][] | undefined
): number[] {
  if (!Array.isArray(itemIds) || itemIds.length === 0) return [];
  const first = itemIds[0];
  const rows = Array.isArray(first)
    ? (itemIds as number[][])
    : [itemIds as number[]];
  return rows
    .flat()
    .map(id => (typeof id === 'number' ? id : Number(id)))
    .filter(id => Number.isFinite(id) && id > 0);
}

function configuredGroupCount(
  itemIds: number[] | number[][] | undefined
): number {
  if (!Array.isArray(itemIds) || itemIds.length === 0) return 0;
  if (!Array.isArray(itemIds[0])) return 0;
  return (itemIds as number[][]).filter(
    row => flattenConfiguredExposureItemIds(row).length > 0
  ).length;
}

/**
 * Values the device should publish in itemShown / itemFeedback.itemIds:
 * catalog ids when not multi-item, 1-based group indices when multi-item.
 */
export function expectedReceivedValues(
  isMultiItem: boolean,
  configuredItemIds: number[] | number[][] | undefined
): number[] {
  if (!isMultiItem) {
    return flattenConfiguredExposureItemIds(configuredItemIds);
  }
  const count = configuredGroupCount(configuredItemIds);
  return Array.from({ length: count }, (_, index) => index + 1);
}

/** Raw numeric itemIds from an Ably itemShown / itemFeedback payload. Does not remap. */
export function extractRawExposureItemId(
  payload: Record<string, unknown>
): number | null {
  const raw =
    payload.itemIds ?? payload.itemId ?? payload.foodIds ?? payload.foodId;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function classifyExposureDeviceEvent(
  input: ExposureDeviceEventCheckInput
): ExposureDeviceEventVerdict {
  const expected = expectedReceivedValues(
    input.isMultiItem,
    input.configuredItemIds
  );
  return expected.includes(input.receivedItemId) ? 'in-list' : 'not-in-list';
}

export function summarizeExposureDeviceEvents(input: {
  isMultiItem: boolean;
  configuredItemIds: number[] | number[][] | undefined;
  receivedItemIds: number[];
}): { missing: number[]; extra: number[] } {
  const expected = expectedReceivedValues(
    input.isMultiItem,
    input.configuredItemIds
  );
  const received = new Set(input.receivedItemIds);
  const expectedSet = new Set(expected);
  const missing = expected.filter(id => !received.has(id));
  const extra = [
    ...new Set(input.receivedItemIds.filter(id => !expectedSet.has(id))),
  ];
  return { missing, extra };
}

export type ExposureItemCoverage = {
  expected: number[];
  expectedCount: number;
  coveredCount: number;
  missingAny: number[];
  missingShown: number[];
  missingFeedback: number[];
  extra: number[];
  /**
   * Single-item only: device stream looks like expectedId+1
   * (missing first configured id, extra lastConfigured+1).
   */
  possibleOffByOne: boolean;
};

/** Coverage of shown/feedback vs configured ids (or 1-based groups). */
export function analyzeExposureItemCoverage(input: {
  isMultiItem: boolean;
  configuredItemIds: number[] | number[][] | undefined;
  shownIds: number[];
  feedbackIds: number[];
}): ExposureItemCoverage {
  const expected = expectedReceivedValues(
    input.isMultiItem,
    input.configuredItemIds
  );
  const shown = new Set(input.shownIds);
  const feedback = new Set(input.feedbackIds);
  const anyReceived = new Set([...shown, ...feedback]);
  const expectedSet = new Set(expected);

  const missingAny = expected.filter(id => !anyReceived.has(id));
  const missingShown = expected.filter(id => !shown.has(id));
  const missingFeedback = expected.filter(id => !feedback.has(id));
  const extra = [
    ...new Set(
      [...anyReceived].filter(id => !expectedSet.has(id)).sort((a, b) => a - b)
    ),
  ];

  let possibleOffByOne = false;
  if (!input.isMultiItem && expected.length >= 2) {
    const first = expected[0];
    const last = expected[expected.length - 1];
    possibleOffByOne =
      missingAny.length === 1 &&
      missingAny[0] === first &&
      extra.length === 1 &&
      extra[0] === last + 1;
  }

  return {
    expected,
    expectedCount: expected.length,
    coveredCount: expected.length - missingAny.length,
    missingAny,
    missingShown,
    missingFeedback,
    extra,
    possibleOffByOne,
  };
}
